import _ from "lodash";
import React from "react";
import classNames from "classnames";

import { generateRandomId } from "Utils";
import {
  ALPHA,
  MAXIMUM,
  MIDDLE,
  MINIMUM,
  CUSTOM,
  REGULAR,
  SPECIAL,
  DEPENDENT,
  NUMERIC,
  DP_IB_DEFINED,
  MYP_FINAL_GRADE,
  MYP_IB_DEFINED,
} from "Constants/stringConstants";
import {
  getHeadersWithScaledWidths,
  getShouldHideDefaultLevelInGradeScale,
} from "GradingSetupV2/modules/Utils";
import { getErrorMessage } from "../../routes/GradeScales/modules/GradeScalesUtils";

import { Dropdown, DropdownMenu, Tooltip } from "@toddle-edu/ds-web";
import { CaretDownFilled, InformationOutlined } from "@toddle-edu/ds-icons";

import classes from "./GradeScaleDatagrid.scss";

const headerCellClass = classNames("text-label", classes.headerCell);
const subHeaderCellClass = classNames("text-body", classes.subHeaderCell);

const DATA_GRID_DEFAULT_WIDTH = 800;
const HEADER_ROW_HEIGHT = 40;

const styles = {
  subheaderStyles: {
    paddingTop: "6px",
    paddingBottom: "6px",
  },
  alignLeft: {
    align: "left",
  },
  alignCenter: {
    justifyContent: "center",
  },
  addWidth: {
    width: "200px",
  },
  tableStyle: {
    borderCollapse: "collapse",
    tableLayout: "fixed",
    width: "100%",
  },
  thStyle: {
    height: `${HEADER_ROW_HEIGHT}px`,
    padding: "0 8px",
    backgroundColor: "var(--surface-secondary-enabled)",
    borderRight: "1px solid var(--border-secondary)",
    borderBottom: "1px solid var(--border-secondary)",
    boxSizing: "border-box",
    overflow: "hidden",
    verticalAlign: "middle",
  },
};

export const colorPalette = [
  "var(--green-600)",
  "var(--blue-600)",
  "var(--yellow-600)",
  "var(--orange-600)",
];

export const calculateGradeValue = ({
  gradeValueMethod,
  rows,
  currentRowIndex,
}) => {
  const currentRow = rows[currentRowIndex];
  const prevRow = rows[currentRowIndex - 1];

  const currentCutoff = _.chain(currentRow)
    .get("Cutoff.value", "0")
    .toNumber()
    .value();

  if (currentRowIndex === 0) {
    switch (gradeValueMethod) {
      case MAXIMUM:
        return "100";
      case MINIMUM:
        return _.toString(_.min([currentCutoff, 100]));
      case MIDDLE:
        return _.toString(_.min([_.ceil((100 + currentCutoff) / 2), 100]));
      default:
        return "0";
    }
  }

  const prevCutoff = _.chain(prevRow)
    .get("Cutoff.value", "0")
    .toNumber()
    .value();

  switch (gradeValueMethod) {
    case MAXIMUM:
      return _.toString(_.max([prevCutoff - 1, 0]));
    case MINIMUM:
      return _.toString(_.min([currentCutoff, 100]));
    case MIDDLE:
      return _.toString(_.min([_.ceil((currentCutoff + prevCutoff) / 2), 100]));
    default:
      return "0";
  }
};

export const recalculateGradeScaleValues = ({
  data,
  gradeValueMethod,
  differentGradebookValuesForLevels,
}) => {
  const updatedData = _.cloneDeep(data);

  updatedData.forEach((row, rowIndex) => {
    const scores = _.get(row, "gradeScaleValueScores", []);
    scores.forEach((score, scoreIdx) => {
      const subjectLevelId = _.get(score, "subjectLevel.id", null);
      const methodForLevel = differentGradebookValuesForLevels
        ? _.get(
            _.find(gradeValueMethod, item => {
              return _.get(item, "subjectLevel.id", null) === subjectLevelId;
            }),
            "gradeValueCalculateType",
            MIDDLE
          )
        : gradeValueMethod;

      let maxValue = 100;
      if (rowIndex > 0) {
        const prevRow = updatedData[rowIndex - 1];
        const prevScore = _.find(
          _.get(prevRow, "gradeScaleValueScores", []),
          s => _.get(s, "subjectLevel.id", null) === subjectLevelId
        );
        maxValue = _.get(prevScore, "minValue", 0);
      }

      _.set(row, ["gradeScaleValueScores", scoreIdx, "maxValue"], maxValue);

      // Calculate gradeValue if not CUSTOM
      if (methodForLevel !== CUSTOM) {
        // Prepare rows for this level
        const rowsForLevel = updatedData.map(r => {
          const s =
            _.find(
              _.get(r, "gradeScaleValueScores", []),
              sc =>
                _.get(sc, "subjectLevel.id", null) ===
                _.get(score, "subjectLevel.id", null)
            ) || {};
          return {
            Cutoff: { value: _.get(s, "minValue", 0) },
            GradeValue: { value: _.get(s, "gradeValue", 0) },
          };
        });
        const calculatedValue = calculateGradeValue({
          rows: rowsForLevel,
          currentRowIndex: rowIndex,
          gradeValueMethod: methodForLevel,
        });
        _.set(
          row,
          ["gradeScaleValueScores", scoreIdx, "gradeValue"],
          parseFloat(calculatedValue) || 0
        );
      }
    });
  });

  return updatedData;
};

const getHeaders = ({
  t,
  gradeScaleType,
  showMasteryLevel,
  showCreditGpaCalculation,
  differentGradebookValuesForLevels,
  levels,
  showActions,
  calculateStandardGradesUsingNumeric,
  lengthOfSelectedDependentGradeScales,
  shouldHideDefaultLevel,
}) => {
  const gradeHeaders = [
    {
      columnKey: "Grades",
      colSpan:
        showMasteryLevel && !calculateStandardGradesUsingNumeric
          ? 5
          : !showMasteryLevel && calculateStandardGradesUsingNumeric
          ? 3
          : 4,
      id: "Grades",
      title: <div className={headerCellClass}>{t("common:grades")}</div>,
    },
  ];

  const gradebookValueHeaders = [
    ...(shouldHideDefaultLevel
      ? []
      : [
          {
            columnKey: "Gradebook",
            id: "Gradebook",
            colSpan: 2,
            title: (
              <div className={headerCellClass}>
                {differentGradebookValuesForLevels
                  ? t("common:default")
                  : t("academicSetup:gradebook_values")}
              </div>
            ),
          },
        ]),
    ...(differentGradebookValuesForLevels
      ? _.map(levels, level => {
          return {
            columnKey: "Gradebook",
            id: _.get(level, "id"),
            colSpan: 2,
            title: (
              <div className={headerCellClass}>
                {_.get(level, "reportDisplayName") ||
                  _.get(level, "abbreviation") ||
                  _.get(level, "label")}
              </div>
            ),
          };
        })
      : []),
  ];

  const creditGpaCalculationHeaders = [
    {
      columnKey: "CreditGpaCalculation",
      id: "CreditGpaCalculation",
      colSpan: 2,
      title: (
        <div className={headerCellClass}>
          {t("academicSetup:gpa_calculation")}
        </div>
      ),
    },
  ];

  const dependentGradeScaleHeaders = [
    {
      columnKey: "DependentGradeScale",
      id: "DependentGradeScale",
      colSpan: lengthOfSelectedDependentGradeScales,
      title: (
        <div className={headerCellClass}>
          {t("academicSetup:other_grade_scales")}
        </div>
      ),
    },
  ];

  // const specialGradeScaleHeaders = [
  //   {
  //     columnKey: "GradeValuesForCalculatedGrades",
  //     colSpan: 3,
  //     id: "GradeValuesForCalculatedGrades",
  //     title: <div>Gradebook values for calculated grades</div>,
  //   },
  //   {
  //     columnKey: "GradeValuesForAssignments",
  //     colSpan: 3,
  //     id: "GradeValuesForAssignments",
  //     title: <div>Gradebook values for assignments</div>,
  //   },
  // ];

  const actionHeaders = [
    {
      columnKey: "Actions",
      id: "Actions",
      colSpan: 2,
      title: <div className={headerCellClass}>{t("common:actions")}</div>,
    },
  ];

  const headers = [
    ...gradeHeaders,
    ...(_.includes([REGULAR, DP_IB_DEFINED], gradeScaleType)
      ? gradebookValueHeaders
      : []),
    ...(showCreditGpaCalculation ? creditGpaCalculationHeaders : []),
    ...(gradeScaleType === DEPENDENT ? dependentGradeScaleHeaders : []),
    // ...(gradeScaleType === SPECIAL ? specialGradeScaleHeaders : []),
    ...(showActions ? actionHeaders : []),
  ];

  return _.map(headers, header => ({
    ...header,
    rowSpan: 1,
    isLeaf: false,
    order: 0,
  }));
};

const getGradeValueOptions = t => {
  return [
    {
      key: MINIMUM,
      label: t("academicSetup:minimum_value"),
      subText: t("academicSetup:minimum_value_cutoff_percentage_description"),
    },
    {
      key: MAXIMUM,
      label: t("academicSetup:maximum_value"),
      subText: t("academicSetup:maximum_value_cutoff_percentage_description"),
    },
    {
      key: MIDDLE,
      label: t("academicSetup:middle_value"),
      subText: t("academicSetup:middle_value_cutoff_percentage_description"),
    },
    {
      key: CUSTOM,
      label: t("common:custom"),
      subText: t("academicSetup:custom_grade_value_description"),
    },
  ];
};

const renderGradeValueDropdown = ({
  t,
  gradeValueLabel,
  gradeValueMethod,
  gradeValueOptions,
  handleGradeValueChange,
  isEditMode,
}) => {
  return (
    <Dropdown
      dsVersion="2.0"
      overlay={
        <DropdownMenu
          dsVersion="2.0"
          showSelection
          value={gradeValueMethod}
          selectionType="radio"
          options={gradeValueOptions}
          hasMultilineOptions
          onClick={handleGradeValueChange}
        />
      }
      disabled={!isEditMode}
      getPopupContainer={() => document.body}
    >
      <div className={classes.dropdownButtonContainer}>
        <div className={classes.dropdownButtonText}>
          <div className={classNames("text-body", classes.textSubtle)}>
            {t("academicSetup:grade_value")}
          </div>
          <div
            className={classNames("text-body-xs", classes.gradeValueContainer)}
          >
            {gradeValueLabel}
            <div className={classes.caretIcon}>
              <CaretDownFilled size="xxx-small" variant="subtle" />
            </div>
          </div>
        </div>
        {isEditMode && (
          <div className={classes.caretIcon}>
            <Tooltip
              tooltip={t("academicSetup:grade_value_tooltip")}
              overlayInnerStyle={styles.addWidth}
              dsVersion="2.0"
            >
              <InformationOutlined size="xxx-small" />
            </Tooltip>
          </div>
        )}
      </div>
    </Dropdown>
  );
};

const getGradebookValueSubHeaders = ({
  t,
  differentGradebookValuesForLevels,
  levels,
  columnWidthMap,
  gradeValueMethod,
  handleGradeValueChange,
  isEditMode,
  isFromCourseConfig,
  gradeScaleType,
  shouldHideDefaultLevel,
}) => {
  if (gradeScaleType === DP_IB_DEFINED && !isFromCourseConfig) {
    return [];
  }

  if (!_.includes([REGULAR, DP_IB_DEFINED, MYP_FINAL_GRADE], gradeScaleType)) {
    return [];
  }

  const gradeValueOptions = getGradeValueOptions(t);

  let levelWiseSubHeaders = [];

  const hideGradeValue = gradeScaleType === MYP_FINAL_GRADE;
  const conditionalPercentage =
    gradeScaleType === MYP_FINAL_GRADE ? "" : " (%)";

  if (differentGradebookValuesForLevels) {
    levelWiseSubHeaders = _.flatMap(levels, level => {
      let methodForLevel = MIDDLE;
      if (_.isArray(gradeValueMethod)) {
        methodForLevel = _.get(
          _.find(
            gradeValueMethod,
            item => _.get(item, "subjectLevel.id", null) === level.id
          ),
          "gradeValueCalculateType",
          MIDDLE
        );
      } else if (_.isString(gradeValueMethod)) {
        methodForLevel = gradeValueMethod;
      }
      const gradeValueLabel = _.get(
        _.find(gradeValueOptions, { key: methodForLevel }),
        "label"
      );
      return [
        {
          columnKey: "Cutoff",
          id: _.get(level, "id") + "_Cutoff",
          colspan: 1,
          rowSpan: 1,
          order: 2,
          width: columnWidthMap?.Cutoff || 160,
          minWidth: 110,
          isLeaf: true,
          title: (
            <div className={subHeaderCellClass}>
              {t("academicSetup:cutoff")} {conditionalPercentage}
            </div>
          ),
        },
        ...(!hideGradeValue
          ? [
              {
                columnKey: "GradeValue",
                id: _.get(level, "id") + "_GradeValue",
                colspan: 1,
                rowSpan: 1,
                order: 2,
                width: columnWidthMap?.GradeValue || 160,
                minWidth: 124,
                isLeaf: true,
                outlineOnHover: isEditMode,
                title: renderGradeValueDropdown({
                  t,
                  id: _.get(level, "id") + "_GradeValue",
                  gradeValueLabel,
                  gradeValueMethod: methodForLevel,
                  gradeValueOptions,
                  handleGradeValueChange: value =>
                    handleGradeValueChange(value, level.id),
                  isEditMode,
                }),
              },
            ]
          : []),
      ];
    });
  }

  const _gradeValueMethod = differentGradebookValuesForLevels
    ? _.get(
        _.find(
          gradeValueMethod,
          item => _.get(item, "subjectLevel.id", null) === null
        ),
        "gradeValueCalculateType"
      )
    : gradeValueMethod;

  const gradeValueLabel = _.get(
    _.find(gradeValueOptions, { key: _gradeValueMethod }),
    "label"
  );

  const defaultLevelColumns = shouldHideDefaultLevel
    ? []
    : [
        {
          columnKey: "Cutoff",
          id: "Cutoff",
          colspan: 1,
          rowSpan: 1,
          order: 2,
          width: columnWidthMap?.Cutoff || 160,
          minWidth: 110,
          isLeaf: true,
          title: (
            <div className={subHeaderCellClass}>
              {t("academicSetup:cutoff")} {conditionalPercentage}
            </div>
          ),
        },
        ...(!hideGradeValue
          ? [
              {
                columnKey: "GradeValue",
                id: "GradeValue",
                colspan: 1,
                rowSpan: 1,
                order: 2,
                width: columnWidthMap?.GradeValue || 160,
                minWidth: 124,
                isLeaf: true,
                outlineOnHover: isEditMode,
                title: renderGradeValueDropdown({
                  t,
                  id: "GradeValue",
                  gradeValueLabel,
                  gradeValueMethod: _gradeValueMethod,
                  gradeValueOptions,
                  handleGradeValueChange,
                  isEditMode,
                }),
              },
            ]
          : []),
      ];

  return [...defaultLevelColumns, ...levelWiseSubHeaders];
};

const getSubHeaders = ({
  t,
  isFromCourseConfig,
  gradeScaleType,
  gradeType,
  showMasteryLevel,
  showCreditGpaCalculation,
  gradeValueMethod,
  handleGradeValueChange,
  selectedDependentGradeScales,
  selectedDependentGradeScalesDetails,
  differentGradebookValuesForLevels,
  levels,
  showActions,
  calculateStandardGradesUsingNumeric,
  isEditMode,
  columnWidthMap,
  containerWidth,
  shouldHideDefaultLevel,
  isAbbreviationLengthLimited,
}) => {
  const gradeSubHeaders = [
    {
      columnKey: "abbreviation",
      id: "abbreviation",
      colspan: 1,
      rowSpan: 1,
      order: 2,
      width:
        columnWidthMap?.abbreviation ||
        (gradeScaleType === SPECIAL ? 200 : 120),
      minWidth: 120,
      isLeaf: true,
      title: (
        <div className={subHeaderCellClass}>
          {gradeType === NUMERIC
            ? t("common:numeric_value")
            : isAbbreviationLengthLimited
            ? t("academicSetup:abbreviation_max_4")
            : t("academicSetup:abbreviation")}
        </div>
      ),
    },
    ...(!calculateStandardGradesUsingNumeric
      ? [
          {
            columnKey: "label",
            id: "label",
            colspan: 1,
            rowSpan: 1,
            order: 2,
            width:
              columnWidthMap?.label || (gradeScaleType === SPECIAL ? 200 : 100),
            minWidth: 80,
            isLeaf: true,
            title: (
              <div className={subHeaderCellClass} style={styles.alignLeft}>
                {t("worksheet:label")}
              </div>
            ),
          },
        ]
      : []),
    {
      columnKey: "description",
      id: "description",
      colspan: 1,
      rowSpan: 1,
      order: 2,
      width:
        columnWidthMap?.description ||
        (gradeScaleType === REGULAR || gradeScaleType === SPECIAL ? 320 : 180),
      minWidth: 100,
      isLeaf: true,
      title: (
        <div className={subHeaderCellClass}>{t("common:description")}</div>
      ),
    },
    {
      columnKey: "color",
      id: "color",
      colspan: 1,
      rowSpan: 1,
      order: 2,
      width: columnWidthMap?.color || 60,
      minWidth: 60,
      isLeaf: true,
      title: (
        <div className={subHeaderCellClass} style={styles.alignCenter}>
          {t("common:color")}
        </div>
      ),
    },
    ...(showMasteryLevel
      ? [
          {
            columnKey: "MasteryLevel",
            id: "MasteryLevel",
            colspan: 1,
            rowSpan: 1,
            order: 2,
            width: 90,
            minWidth: 90,
            isLeaf: true,
            title: (
              <div className={subHeaderCellClass} style={styles.alignCenter}>
                <div>{t("academicSetup:mastery")}</div>
                <Tooltip
                  tooltip={t("academicSetup:mastery_tooltip")}
                  overlayInnerStyle={styles.addWidth}
                  dsVersion="2.0"
                >
                  <InformationOutlined size="xxx-small" />
                </Tooltip>
              </div>
            ),
          },
        ]
      : []),
  ];

  const gradebookValueSubHeaders = getGradebookValueSubHeaders({
    t,
    differentGradebookValuesForLevels,
    levels,
    columnWidthMap,
    gradeValueMethod,
    handleGradeValueChange,
    isEditMode,
    isFromCourseConfig,
    gradeScaleType,
    shouldHideDefaultLevel,
  });

  const creditGpaCalculationSubHeaders = [
    {
      columnKey: "GradePoint",
      id: "GradePoint",
      colspan: 1,
      rowSpan: 1,
      order: 2,
      width: 120,
      minWidth: 120,
      isLeaf: true,
      title: (
        <div className={subHeaderCellClass} style={styles.alignCenter}>
          <div>{t("common:grade_point")}</div>
          <Tooltip
            tooltip={t("academicSetup:numeric_value_tooltip")}
            overlayInnerStyle={styles.addWidth}
            dsVersion="2.0"
          >
            <InformationOutlined size="xxx-small" />
          </Tooltip>
        </div>
      ),
    },
    {
      columnKey: "AddedValue",
      id: "AddedValue",
      colspan: 1,
      rowSpan: 1,
      order: 2,
      width: 120,
      minWidth: 120,
      isLeaf: true,
      title: (
        <div className={subHeaderCellClass} style={styles.alignCenter}>
          <div>{t("academicSetup:added_value")}</div>
          <Tooltip
            tooltip={t("academicSetup:added_value_tooltip")}
            overlayInnerStyle={styles.addWidth}
            dsVersion="2.0"
          >
            <InformationOutlined size="xxx-small" />
          </Tooltip>
        </div>
      ),
    },
  ];

  const dependentGradeScaleSubHeaders = _.map(
    selectedDependentGradeScales,
    gradeScaleId => {
      const gradeScale = _.find(
        selectedDependentGradeScalesDetails,
        item => _.get(item, "id") === gradeScaleId
      );

      return {
        columnKey: "dependentGradeScale",
        id: gradeScaleId,
        colspan: 1,
        rowSpan: 1,
        order: 2,
        width: 180,
        minWidth: 100,
        isLeaf: true,
        title: (
          <div className={subHeaderCellClass}>
            {_.get(gradeScale, "templateName", "")}
          </div>
        ),
      };
    }
  );

  // const specialGradeScaleSubHeaders = [
  //   {
  //     columnKey: "UseAsFinalGrade",
  //     id: "UseAsFinalGrade",
  //     colspan: 1,
  //     rowSpan: 1,
  //     order: 2,
  //     width: 140,
  //     isLeaf: true,
  //     title: <div className={subHeaderCellClass}>Use as final grade</div>,
  //   },
  //   {
  //     columnKey: "ValueForCalculatedGrades",
  //     id: "ValueForCalculatedGrades",
  //     colspan: 1,
  //     rowSpan: 1,
  //     order: 2,
  //     width: 80,
  //     isLeaf: true,
  //     title: <div className={subHeaderCellClass}>% value</div>,
  //   },
  //   {
  //     columnKey: "NumericValueForCalculatedGrades",
  //     id: "NumericValueForCalculatedGrades",
  //     colspan: 1,
  //     rowSpan: 1,
  //     order: 2,
  //     width: 120,
  //     isLeaf: true,
  //     title: <div className={subHeaderCellClass}>Numeric value</div>,
  //   },
  //   {
  //     columnKey: "UseInTasks",
  //     id: "UseInTasks",
  //     colspan: 1,
  //     rowSpan: 1,
  //     order: 2,
  //     width: 140,
  //     isLeaf: true,
  //     title: <div className={subHeaderCellClass}>Use in tasks</div>,
  //   },
  //   {
  //     columnKey: "ValueForAssignments",
  //     id: "ValueForAssignments",
  //     colspan: 1,
  //     rowSpan: 1,
  //     order: 2,
  //     width: 80,
  //     isLeaf: true,
  //     title: <div className={subHeaderCellClass}>% value</div>,
  //   },
  //   {
  //     columnKey: "NumericValueForAssignments",
  //     id: "NumericValueForAssignments",
  //     colspan: 1,
  //     rowSpan: 1,
  //     order: 2,
  //     width: 120,
  //     isLeaf: true,
  //     title: <div className={subHeaderCellClass}>Numeric value</div>,
  //   },
  // ];

  const actionSubHeaders = [
    {
      columnKey: "MoveRow",
      id: "MoveRow",
      colspan: 1,
      rowSpan: 1,
      order: 2,
      width: 90,
      minWidth: 90,
      isLeaf: true,
      title: "",
    },
    {
      columnKey: "AddOrDelete",
      id: "AddOrDelete",
      colspan: 1,
      rowSpan: 1,
      order: 2,
      width: 90,
      minWidth: 90,
      isLeaf: true,
      title: "",
    },
  ];

  const subHeaders = [
    ...gradeSubHeaders,
    ...gradebookValueSubHeaders,
    ...(showCreditGpaCalculation ? creditGpaCalculationSubHeaders : []),
    ...(gradeScaleType === DEPENDENT ? dependentGradeScaleSubHeaders : []),
    // TODOGS2: uncomment when backend support added
    // ...(gradeScaleType === SPECIAL
    //   ? specialGradeScaleSubHeaders
    //   : []),
    ...(showActions ? actionSubHeaders : []),
  ];

  // reduce padding in course config
  const filteredSubHeaders = isFromCourseConfig
    ? _.map(subHeaders, item => {
        return {
          ...item,
          styles: styles.subheaderStyles,
        };
      })
    : subHeaders;

  return filteredSubHeaders;
};

const buildCustomHeader = ({
  groupHeadersRow,
  leafHeaders,
  isFromCourseConfig,
}) => {
  const thStyle = isFromCourseConfig
    ? { ...styles.thStyle, backgroundColor: "transparent" }
    : styles.thStyle;

  return (
    <table style={styles.tableStyle}>
      <colgroup>
        {_.map(leafHeaders, cell => (
          <col key={cell.id} style={{ width: `${cell.width}px` }} />
        ))}
      </colgroup>
      <thead>
        {groupHeadersRow && (
          <tr>
            {_.map(groupHeadersRow, cell => (
              <th key={cell.id} colSpan={cell.colSpan || 1} style={thStyle}>
                {cell.title}
              </th>
            ))}
          </tr>
        )}
        <tr>
          {_.map(leafHeaders, cell => (
            <th key={cell.id} style={{ ...thStyle, width: `${cell.width}px` }}>
              {cell.title}
            </th>
          ))}
        </tr>
      </thead>
    </table>
  );
};

export const getDatagridHeaders = ({
  t,
  isFromCourseConfig,
  gradeScaleType,
  gradeType,
  curriculumType,
  showMasteryLevel,
  showCreditGpaCalculation,
  selectedDependentGradeScales,
  selectedDependentGradeScalesDetails,
  showActions,
  gradeValueMethod,
  differentGradebookValuesForLevels,
  levels,
  handleGradeValueChange,
  calculateStandardGradesUsingNumeric = false,
  isEditMode,
  columnWidthMap,
  containerWidth,
  isAbbreviationLengthLimited,
}) => {
  // Calculate shouldHideDefaultLevel once and pass it to all helper functions
  // At course level, hide default for all curricula when levels exist
  const shouldHideDefaultLevel = isFromCourseConfig
    ? !_.isEmpty(levels) && differentGradebookValuesForLevels
    : getShouldHideDefaultLevelInGradeScale({
        levels,
        curriculumType,
        differentGradebookValuesForLevels,
      });

  const headers = getHeaders({
    t,
    gradeScaleType,
    showMasteryLevel,
    showCreditGpaCalculation,
    showActions,
    differentGradebookValuesForLevels,
    levels,
    calculateStandardGradesUsingNumeric,
    lengthOfSelectedDependentGradeScales: _.size(selectedDependentGradeScales),
    shouldHideDefaultLevel,
  });

  const subHeaders = getSubHeaders({
    t,
    isFromCourseConfig,
    gradeScaleType,
    gradeType,
    showMasteryLevel,
    showCreditGpaCalculation,
    gradeValueMethod,
    handleGradeValueChange,
    selectedDependentGradeScales,
    selectedDependentGradeScalesDetails,
    differentGradebookValuesForLevels,
    levels,
    calculateStandardGradesUsingNumeric,
    showActions,
    isEditMode,
    columnWidthMap,
    containerWidth,
    shouldHideDefaultLevel,
    isAbbreviationLengthLimited,
  });

  const showGroupRow =
    _.size(headers) > 1 &&
    (!isFromCourseConfig || differentGradebookValuesForLevels);

  // Scale leaf header widths using the 2D structure (mutates widths in-place)
  getHeadersWithScaledWidths({
    headers: showGroupRow ? [headers, subHeaders] : [subHeaders],
    containerWidth,
    parentDefaultWidth: DATA_GRID_DEFAULT_WIDTH,
    isMultiLevelHeaders: true,
  });

  // subHeaders now has scaled widths
  const leafHeaders = subHeaders;
  const groupHeadersRow = showGroupRow ? headers : null;

  const leafHeadersForCanvas = _.map(leafHeaders, h => ({
    ...h,
    title: "",
  }));

  return {
    headers: leafHeadersForCanvas,
    customHeader: () =>
      buildCustomHeader({ groupHeadersRow, leafHeaders, isFromCourseConfig }),
  };
};

export const getRowData = ({
  t,
  gradeScaleType = REGULAR,
  gradeType = ALPHA,
  gradeValueMethod = MIDDLE,
  showMasteryLevel = false,
  showCreditGpaCalculation = false,
  isFromCourseConfig,
  showActions = true,
  isEditMode = true,
  calculateStandardGradesUsingNumeric = false,
  selectedDependentGradeScales,
  moveRow = () => {},
  addAfterCurrentRow = () => {},
  deleteRow = () => {},
  values = {},
  allGradeScalesData,
  showErrors = false,
  validationFields = {},
  differentGradebookValuesForLevels = false,
  levels = [],
  isGradeScaleBeingUsed = false,
  gradeScaleValuesData = [],
  shouldHideDefaultLevel,
}) => {
  const displaySequence = _.get(values, "displaySequence", 0);
  const rowErrors = _.get(
    validationFields,
    `fieldErrors.row_${displaySequence}`,
    {}
  );

  const isSpecialGradeTemplateRow =
    _.get(values, "type", "REGULAR") === "SPECIAL";
  const color = _.get(values, "color");
  const isMasteryChecked = _.get(
    values,
    "gradeScaleValueScores[0].isMasteryLevel",
    ""
  );

  // Determine mastery level tooltip
  let masteryLevelTooltip = null;
  if (isMasteryChecked) {
    masteryLevelTooltip =
      "Grades marked as mastery (and any levels above it) are automatically set to green";
  } else if (
    showErrors &&
    _.get(validationFields, "fieldErrors.masteryLevel")
  ) {
    masteryLevelTooltip = getErrorMessage(
      "masteryLevel",
      _.get(validationFields, "fieldErrors.masteryLevel")
    );
  }

  const gradesCellData = {
    abbreviation: {
      value: _.get(values, "abbreviation", ""),
      cellType: gradeType === NUMERIC ? "number" : "text",
      placeholder: t("academicSetup:add_abbreviation"),
      styles: styles.alignLeft,
      isDisabled: isEditMode && gradeType === NUMERIC,
      isEditable:
        !isFromCourseConfig &&
        isEditMode &&
        !isSpecialGradeTemplateRow &&
        gradeType !== NUMERIC,
      hasError: showErrors && !!rowErrors.abbreviation,
      tooltip: showErrors
        ? getErrorMessage(
            "abbreviation",
            rowErrors.abbreviation,
            gradeScaleType
          )
        : "",
    },
    ...(!calculateStandardGradesUsingNumeric
      ? {
          label: {
            value: _.get(values, "label", ""),
            cellType: "text",
            placeholder: t("academicSetup:add_grade"),
            styles: styles.alignLeft,
            isEditable:
              !isFromCourseConfig && isEditMode && !isSpecialGradeTemplateRow,
          },
        }
      : {}),
    description: {
      value: _.get(values, "description", ""),
      cellType: "text",
      placeholder: t("academicSetup:add_description"),
      styles: styles.alignLeft,
      isEditable:
        !isFromCourseConfig && isEditMode && !isSpecialGradeTemplateRow,
    },
    color: {
      value: _.isEmpty(color) ? colorPalette[0] : color,
      cellType: "colorPicker",
      isEditable:
        !isFromCourseConfig && isEditMode && !isSpecialGradeTemplateRow,
      isDisabled: false,
    },
    ...(showMasteryLevel && {
      MasteryLevel: {
        checked: isMasteryChecked,
        tooltip: masteryLevelTooltip,
        cellType: "radio",
        hasError:
          showErrors && !!_.get(validationFields, "fieldErrors.masteryLevel"),
        isEditable:
          !isFromCourseConfig && isEditMode && !isSpecialGradeTemplateRow,
        isDisabled: isSpecialGradeTemplateRow,
      },
    }),
  };

  const gradebookValuesCellDataForLevels = {};
  if (differentGradebookValuesForLevels && !_.isEmpty(levels)) {
    _.forEach(levels, level => {
      const score =
        _.find(
          _.get(values, "gradeScaleValueScores", []),
          s => _.get(s, "subjectLevel.id", null) === _.get(level, "id")
        ) || {};

      let methodForLevel = MIDDLE;
      if (_.isArray(gradeValueMethod)) {
        methodForLevel = _.get(
          _.find(
            gradeValueMethod,
            item => _.get(item, "subjectLevel.id", null) === level.id
          ),
          "gradeValueCalculateType",
          MIDDLE
        );
      } else if (_.isString(gradeValueMethod)) {
        methodForLevel = gradeValueMethod;
      }

      gradebookValuesCellDataForLevels[`${_.get(level, "id")}_Cutoff`] = {
        value: isSpecialGradeTemplateRow ? "-" : _.get(score, "minValue", 0),
        cellType: "number",
        suffixText:
          isSpecialGradeTemplateRow || gradeScaleType === MYP_FINAL_GRADE
            ? ""
            : " or higher",
        width: 180,
        styles: styles.alignLeft,
        hasError:
          showErrors && !!_.get(rowErrors, `cutoff.${_.get(level, "id")}`),
        tooltip: showErrors
          ? getErrorMessage(
              "cutoff",
              _.get(rowErrors, `cutoff.${_.get(level, "id")}`),
              gradeScaleType
            )
          : "",
        isEditable: isEditMode && !isSpecialGradeTemplateRow,
        isDisabled: isEditMode && isSpecialGradeTemplateRow,
      };
      gradebookValuesCellDataForLevels[`${_.get(level, "id")}_GradeValue`] = {
        value: isSpecialGradeTemplateRow ? "-" : _.get(score, "gradeValue", 0),
        cellType: "number",
        width: 180,
        styles: styles.alignLeft,
        hasError:
          showErrors && !!_.get(rowErrors, `gradeValue.${_.get(level, "id")}`),
        tooltip: showErrors
          ? getErrorMessage(
              "gradeValue",
              _.get(rowErrors, `gradeValue.${_.get(level, "id")}`),
              gradeScaleType
            )
          : "",
        isEditable:
          methodForLevel === CUSTOM && isEditMode && !isSpecialGradeTemplateRow,
        isDisabled:
          isEditMode &&
          (!(methodForLevel === CUSTOM) || isSpecialGradeTemplateRow),
      };
    });
  }
  const _gradeValueMethod = differentGradebookValuesForLevels
    ? _.get(
        _.find(
          gradeValueMethod,
          item => _.get(item, "subjectLevel.id", null) === null
        ),
        "gradeValueCalculateType"
      )
    : gradeValueMethod;

  const defaultLevelCellData = shouldHideDefaultLevel
    ? {}
    : {
        Cutoff: {
          value: isSpecialGradeTemplateRow
            ? "-"
            : _.get(values, "gradeScaleValueScores[0].minValue", 0),
          cellType: "number",
          suffixText:
            isSpecialGradeTemplateRow || gradeScaleType === MYP_FINAL_GRADE
              ? ""
              : " or higher",
          width: 180,
          styles: styles.alignLeft,
          hasError: showErrors && !!_.get(rowErrors, "cutoff.default"),
          tooltip: showErrors
            ? getErrorMessage(
                "cutoff",
                _.get(rowErrors, "cutoff.default"),
                gradeScaleType
              )
            : "",
          isEditable: isEditMode && !isSpecialGradeTemplateRow,
          isDisabled:
            gradeScaleType === MYP_FINAL_GRADE ||
            (isEditMode && isSpecialGradeTemplateRow),
        },
        GradeValue: {
          value: isSpecialGradeTemplateRow
            ? "-"
            : _.get(values, "gradeScaleValueScores[0].gradeValue", 0),
          cellType: "number",
          width: 180,
          styles: styles.alignLeft,
          hasError: showErrors && !!_.get(rowErrors, "gradeValue.default"),
          tooltip: showErrors
            ? getErrorMessage(
                "gradeValue",
                _.get(rowErrors, "gradeValue.default"),
                gradeScaleType
              )
            : "",
          isEditable:
            _gradeValueMethod === CUSTOM &&
            isEditMode &&
            !isSpecialGradeTemplateRow,
          isDisabled:
            isEditMode &&
            (!(_gradeValueMethod === CUSTOM) || isSpecialGradeTemplateRow),
        },
      };

  const gradebookValuesCellData = {
    ...defaultLevelCellData,
    ...gradebookValuesCellDataForLevels,
  };

  const isAddedValueApplicable = _.get(
    values,
    "gradeScaleValueGradePoints[0].isAddedValueApplicable",
    false
  );

  const creditGpaCalculationCellData = {
    GradePoint: {
      value: _.get(values, "gradeScaleValueGradePoints[0].gradePoint", ""),
      cellType: "number",
      isEditable: isEditMode && !isSpecialGradeTemplateRow,
      isDisabled: isEditMode && isSpecialGradeTemplateRow,
    },
    AddedValue: {
      value: isAddedValueApplicable ? "checked" : "unchecked",
      cellType: "checkbox",
      isEditable: isEditMode && !isSpecialGradeTemplateRow,
      isDisabled: isEditMode && isSpecialGradeTemplateRow,
    },
  };

  const dependentGradeScaleCellData = _.reduce(
    selectedDependentGradeScales,
    (result, gradeScaleId) => {
      const gradeScale = _.find(
        allGradeScalesData,
        edge => _.get(edge, "node.id") === gradeScaleId
      );

      // Get all values selected in the CURRENT row for this grade scale
      const currentRowSelectedValues = _.filter(
        _.get(values, "dependentGradeScaleValues", []),
        item => _.get(item, "gradeScale.id") === gradeScaleId
      );

      // Get ALL selected IDs across ALL rows for this grade scale
      const allSelectedIds = _.chain(gradeScaleValuesData)
        .flatMap(row => _.get(row, "dependentGradeScaleValues", []))
        .filter(item => _.get(item, "gradeScale.id") === gradeScaleId)
        .map(item => _.get(item, "id"))
        .compact()
        .value();

      // Mark options as disabled if they're selected in ANY row
      const gradeScaleValues = _.chain(gradeScale)
        .get("node.gradeScaleEntityDetails[0].gradeScaleValues", [])
        .sortBy("displaySequence")
        .filter(value => {
          // Hide special gradescale rows in dropdown options
          const valueType = _.get(value, "type");
          return valueType !== "SPECIAL";
        })
        .map(value => ({
          id: value.id,
          label: value.abbreviation,
          isDisabled: _.includes(allSelectedIds, value.id),
        }))
        .value();

      const displayValues = _.map(currentRowSelectedValues, item => {
        return {
          id: _.get(item, "id"),
          key: _.get(item, "id"),
          label: _.get(item, "abbreviation"),
        };
      });

      let cellValue = "";
      if (isSpecialGradeTemplateRow) {
        cellValue = "-";
      } else if (!_.isEmpty(displayValues)) {
        cellValue = displayValues;
      }

      result[gradeScaleId] = {
        value: cellValue,
        cellType: "dropdown",
        isMulti: true,
        validInputs: {
          options: gradeScaleValues,
        },
        hasError:
          showErrors && !!_.get(rowErrors, `dependentValues.${gradeScaleId}`),
        tooltip: showErrors
          ? getErrorMessage(
              "dependentValues",
              _.get(rowErrors, `dependentValues.${gradeScaleId}`),
              gradeScaleType
            )
          : "",
        styles: styles.alignLeft,
        isEditable:
          !isFromCourseConfig && isEditMode && !isSpecialGradeTemplateRow,
        isDisabled: isEditMode && isSpecialGradeTemplateRow,
      };
      return result;
    },
    {}
  );

  const specialGradeScaleCellData = {
    UseAsFinalGrade: {
      value: _.get(values, "useAsFinalGrade", false),
      cellType: "radio",
      isEditable: true,
    },
    ValueForCalculatedGrades: {
      value: _.get(values, "valueForCalculatedGrades", 0),
      cellType: "number",
      isEditable: true,
    },
    NumericValueForCalculatedGrades: {
      value: _.get(values, "numericValueForCalculatedGrades", 0),
      cellType: "number",
      isEditable: true,
    },
    UseInTasks: {
      value: _.get(values, "useInTasks", false),
      cellType: "radio",
      isEditable: true,
    },
    ValueForAssignments: {
      value: _.get(values, "valueForAssignments", 0),
      cellType: "number",
      isEditable: true,
    },
    NumericValueForAssignments: {
      value: _.get(values, "numericValueForAssignments", 0),
      cellType: "number",
      isEditable: true,
    },
  };

  const actionCellData = {
    MoveRow: {
      cellType: "icon",
      isEditable: false,
      value: [
        {
          name: "UpArrowOutlined",
          onIconClick: args => moveRow(args),
          spaceAfter: 20,
          isDisabled: isSpecialGradeTemplateRow,
        },
        {
          name: "DownArrowOutlined",
          onIconClick: args => moveRow(args),
          isDisabled: isSpecialGradeTemplateRow,
        },
      ],
    },
    AddOrDelete: {
      cellType: "icon",
      isEditable: false,
      tooltip: isGradeScaleBeingUsed
        ? t(
            "academicSetup:cannot_change_this_setting_once_grade_scale_is_applied"
          )
        : "",
      value: [
        {
          name: "AddCircleOutlined",
          onIconClick: args => addAfterCurrentRow(args),
          spaceAfter: 20,
          isDisabled: isSpecialGradeTemplateRow,
        },
        {
          name: "DeleteOutlined",
          onIconClick: args => deleteRow(args),
          isDisabled: isSpecialGradeTemplateRow || isGradeScaleBeingUsed,
        },
      ],
    },
  };

  const rowData = {
    id: _.get(values, "id", generateRandomId()),
    displaySequence: _.get(values, "displaySequence", 0),
    ...gradesCellData,
    ...(_.includes([REGULAR, DP_IB_DEFINED, MYP_FINAL_GRADE], gradeScaleType)
      ? gradebookValuesCellData
      : {}),
    ...(showCreditGpaCalculation ? creditGpaCalculationCellData : {}),
    ...(gradeScaleType === DEPENDENT ? dependentGradeScaleCellData : {}),
    // ...(gradeScaleType === SPECIAL
    //   ? specialGradeScaleCellData
    //   : {}),
    ...(showActions ? actionCellData : {}),
  };

  return rowData;
};

export const getNewRow = ({
  gradeScaleType,
  differentGradebookValuesForLevels = false,
  levels = [],
  color = null,
  shouldHideDefaultLevel = false,
}) => {
  let gradeScaleValueScoresForLevels = [];
  let gradeScaleValueScores = [];
  if (gradeScaleType === REGULAR) {
    if (differentGradebookValuesForLevels && !_.isEmpty(levels)) {
      gradeScaleValueScoresForLevels = _.map(levels, level => ({
        subjectLevel: {
          id: _.get(level, "id"),
        },
        minValue: 0,
        maxValue: 100,
        gradeValue: 0,
        isMasteryLevel: false,
      }));
    }
    const defaultGradeScaleValueScores = shouldHideDefaultLevel
      ? []
      : [
          {
            minValue: 0,
            maxValue: 100,
            gradeValue: 0,
            isMasteryLevel: false,
          },
        ];
    gradeScaleValueScores = [
      ...defaultGradeScaleValueScores,
      ...gradeScaleValueScoresForLevels,
    ];
  }
  return {
    id: generateRandomId(),
    abbreviation: "",
    label: "",
    description: "",
    color: color ? color : colorPalette[_.random(0, _.size(colorPalette) - 1)],
    ...(gradeScaleType === REGULAR ? { gradeScaleValueScores } : {}),
    ...(gradeScaleType === REGULAR
      ? {
          gradeScaleValueGradePoints: [
            {
              gradePoint: "",
              isAddedValueApplicable: false,
            },
          ],
        }
      : {}),
    // Initialize dependentGradeScaleValues as empty array for DEPENDENT grade scales
    // The actual dependent grade scale values will now be added when the user selects them via the dropdown component
    ...(gradeScaleType === DEPENDENT
      ? {
          dependentGradeScaleValues: [],
        }
      : {}),
  };
};

const getOrderedGradeScaleValuesByType = ({
  gradeScaleType,
  gradeScaleValuesData,
}) => {
  // MYP IB defined scale is shown in 0 - 8 order in the platform but here it needs to shown from 8 -0
  // so apply custom order for it here
  if (gradeScaleType === MYP_IB_DEFINED) {
    const nonSpecialValues = _.filter(
      gradeScaleValuesData,
      item => item?.type !== SPECIAL
    );
    const specialValues = _.filter(gradeScaleValuesData, { type: SPECIAL });

    const orderedNonSpecialValues = _.orderBy(
      nonSpecialValues,
      ["label"],
      ["desc"]
    );

    const reorderedValues = [];
    if (!_.isEmpty(nonSpecialValues)) {
      reorderedValues.push(...orderedNonSpecialValues);
    }
    if (!_.isEmpty(specialValues)) {
      reorderedValues.push(...specialValues);
    }

    return reorderedValues;
  }

  return gradeScaleValuesData;
};

export const getGridData = ({
  t,
  isFromCourseConfig,
  gradeScaleValuesData,
  gradeScaleType,
  gradeType,
  gradeValueMethod,
  showMasteryLevel = false,
  showCreditGpaCalculation = false,
  calculateStandardGradesUsingNumeric = false,
  moveRow,
  addAfterCurrentRow,
  deleteRow,
  showActions = true,
  isEditMode = true,
  selectedDependentGradeScales = [],
  allGradeScalesData,
  showErrors,
  validationFields,
  differentGradebookValuesForLevels = false,
  levels = [],
  isGradeScaleBeingUsed = false,
  shouldHideDefaultLevel,
}) => {
  const orderedGradeScaleValuesData = getOrderedGradeScaleValuesByType({
    gradeScaleType,
    gradeScaleValuesData,
  });

  return _.map(orderedGradeScaleValuesData, gradeValue =>
    getRowData({
      t,
      isFromCourseConfig,
      gradeScaleType,
      gradeType,
      showMasteryLevel,
      showCreditGpaCalculation,
      gradeValueMethod,
      isEditMode,
      showActions,
      selectedDependentGradeScales,
      calculateStandardGradesUsingNumeric,
      moveRow,
      addAfterCurrentRow,
      deleteRow,
      values: gradeValue,
      allGradeScalesData,
      showErrors,
      validationFields,
      differentGradebookValuesForLevels,
      levels,
      isGradeScaleBeingUsed,
      gradeScaleValuesData,
      shouldHideDefaultLevel,
    })
  );
};
